<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">

        <section class="intro">
          <p>
            El <strong>tour virtual 360°</strong> se ha convertido en una herramienta imprescindible para el marketing digital en 2026. Ya seas agente inmobiliario, dueño de un hotel, comerciante o simplemente curioso, esta guía completa te explica todo lo que necesitas saber — desde los fundamentos técnicos hasta la publicación y el posicionamiento en Google — sin conocimientos previos.
          </p>
        </section>

        <section>
          <h2>¿Qué es un tour virtual 360°?</h2>
          <p>
            Un tour virtual 360° es una experiencia inmersiva en línea que permite al usuario moverse por un espacio como si estuviera físicamente allí. Gracias a fotografías panorámicas esféricas tomadas desde todos los ángulos, el tour recrea el espacio en un entorno navegable en tres dimensiones desde cualquier navegador o smartphone.
          </p>
          <p>
            A diferencia de un vídeo estándar donde la cámara dicta el punto de vista, el tour virtual deja al visitante libre de mirar donde quiera, desplazarse de habitación en habitación e interactuar con puntos de interés (<em>hotspots</em>). Esta libertad explica por qué los tours virtuales generan en promedio <strong>5 a 10 veces más engagement</strong> que una galería de fotos tradicional.
          </p>
        </section>

        <section>
          <h2>Equipo necesario para crear un tour virtual</h2>

          <h3>Opción 1 — Tu smartphone (recomendado para principiantes)</h3>
          <p>
            Desde 2024, los smartphones de gama alta son capaces de producir panoramas esféricos de calidad profesional gracias a la IA integrada. Una aplicación como <strong>Travvir</strong> ensambla automáticamente las fotos y corrige las uniones. Coste adicional: 0 €.
          </p>

          <h3>Opción 2 — Cámara 360° dedicada</h3>
          <p>
            Modelos como la Insta360 X4, la Ricoh Theta Z1 o la GoPro Max ofrecen mayor calidad de imagen y una captura más rápida. Ideal para profesionales que crean varios tours por semana. Presupuesto: 400–1.200 €.
          </p>

          <h3>Opción 3 — Réflex/mirrorless con objetivo fisheye</h3>
          <p>
            Para un acabado de estudio, los fotógrafos profesionales utilizan una cámara full-frame con dos capturas en fisheye por posición. Máxima calidad pero flujo de trabajo complejo — reservado para proyectos de alta gama.
          </p>
        </section>

        <section>
          <h2>Guía paso a paso: crear tu primer tour virtual</h2>

          <h3>Paso 1 — Preparar el espacio</h3>
          <p>Ordena, despeja y abre las persianas. Enciende todas las luces y cierra las ventanas para evitar destellos. Un espacio limpio aumenta drásticamente la calidad percibida del tour.</p>

          <h3>Paso 2 — Capturar los panoramas</h3>
          <p>Colócate en el centro de cada habitación. Con Travvir, la IA te guía: detecta cuándo has cubierto suficientes ángulos y te señala las zonas que faltan en tiempo real. Calcula unos 2–3 minutos por habitación.</p>

          <h3>Paso 3 — Ensamblaje automático por IA</h3>
          <p>Travvir ensambla las imágenes en un panorama equirrectangular nítido, corrige las diferencias de iluminación y elimina el reflejo del fotógrafo. Resultado: una imagen 360° de calidad profesional en minutos.</p>

          <h3>Paso 4 — Crear el tour interactivo</h3>
          <p>Conecta los panoramas entre sí mediante hotspots de navegación. Añade etiquetas (nombre de las habitaciones, superficie, características destacadas), ventanas de información y música ambiental si lo deseas.</p>

          <h3>Paso 5 — Publicar y compartir</h3>
          <p>Travvir genera un enlace compartible (compatible con ordenador, móvil y tablet) y un código de integración iframe. Añade el tour a tus anuncios inmobiliarios, tu sitio web, tu perfil de Google Business y compártelo en redes sociales.</p>
        </section>

        <section>
          <h2>Formatos de tour virtual: comparativa</h2>
          <table>
            <thead>
              <tr><th>Formato</th><th>Uso</th><th>Coste</th><th>Calidad</th></tr>
            </thead>
            <tbody>
              <tr><td>Smartphone + IA (Travvir)</td><td>Inmobiliaria, PYME, hostelería</td><td>Prueba gratuita</td><td>Excelente</td></tr>
              <tr><td>Cámara 360° dedicada</td><td>Profesional recurrente</td><td>400–1.200 €</td><td>Muy alta</td></tr>
              <tr><td>Matterport</td><td>Inmobiliaria premium</td><td>500 €+/mes</td><td>Premium 3D</td></tr>
              <tr><td>Fotógrafo profesional</td><td>Grandes cuentas</td><td>200–600 €/propiedad</td><td>Estudio</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Cómo difundir y posicionar tu tour virtual</h2>
          <ul>
            <li><strong>Perfil de Google Business</strong> — integrar un tour mejora tu posición en los resultados locales de Google Maps.</li>
            <li><strong>Portales inmobiliarios</strong> — Idealista, Fotocasa, Habitaclia aceptan enlaces de tour virtual.</li>
            <li><strong>Sitio web</strong> — integra el código iframe en tus páginas de propiedades o en tu página de inicio.</li>
            <li><strong>Redes sociales</strong> — comparte el enlace en LinkedIn, Facebook, Instagram Stories y WhatsApp Business.</li>
            <li><strong>Campañas de email</strong> — un tour virtual en un email aumenta la tasa de clics un 70% de media.</li>
          </ul>
        </section>

        <section>
          <h2>Preguntas frecuentes</h2>

          <h3>¿Necesito una conexión a Internet rápida para ver un tour virtual?</h3>
          <p>No. Travvir optimiza la carga por teselas: el tour se carga progresivamente, incluso con una conexión 4G estándar. La experiencia es fluida a partir de 5 Mbps.</p>

          <h3>¿Los tours virtuales son compatibles con dispositivos VR?</h3>
          <p>Sí. Los tours creados con Travvir son compatibles con Google Cardboard y los cascos Meta Quest mediante el modo WebXR, sin ninguna configuración adicional.</p>

          <h3>¿Travvir ofrece una prueba gratuita?</h3>
          <p>Sí. Travvir ofrece una prueba gratuita para crear y publicar tours virtuales. Descarga la app Travvir para empezar tu prueba gratuita. Los planes profesionales desbloquean funciones avanzadas como branding personalizado, analíticas y dominio propio.</p>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "tour-virtual-360-guia-completa",
);

useBlogSeo({
  title: "Tour Virtual 360°: Guía Completa para Principiantes (2026) | Travvir",
  description:
    "Descubre todo sobre el tour virtual 360°: equipo, pasos de creación, difusión y posicionamiento en Google. Guía completa para principiantes — crea tu primer tour con tu smartphone hoy mismo.",
  keywords:
    "tour virtual 360, crear tour virtual, guía tour virtual, tour virtual principiantes, tour virtual smartphone, software tour virtual gratis, visita virtual 360 grados",
  slug: "tour-virtual-360-guia-completa",
  image: postData.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question: "¿Necesito una conexión a Internet rápida para ver un tour virtual?",
      answer: "No. Travvir optimiza la carga por teselas, la experiencia es fluida a partir de 5 Mbps incluso con 4G.",
    },
    {
      question: "¿Los tours virtuales son compatibles con dispositivos VR?",
      answer: "Sí. Los tours de Travvir son compatibles con Google Cardboard y Meta Quest mediante WebXR.",
    },
    {
      question: "¿Travvir ofrece una prueba gratuita?",
      answer: "Sí. Travvir ofrece una prueba gratuita para crear y publicar tours. Descarga la app Travvir para empezar tu prueba gratuita. Los planes pro desbloquean branding personalizado, analíticas y dominio propio.",
    },
  ],
});

</script>
